/**
 * JointOwnerAddDialog — account-scoped Joint Owner add flow.
 *
 * Fires from: Products tab row action on a deposit/CD account.
 *
 * Two-step UX:
 *   Step 1: "Pick existing CIF or capture new person"
 *     - GlobalCustomerSearch for existing CIF lookup
 *     - "+ Capture new person" button opens PersonDialog (category dispatcher
 *       resolves to IndividualPersonDialog) in mode="new".
 *   Step 2: Confirm + write CustRelAdd via useJointOwnerAdd.
 *
 * Reuses the unified PersonDialog — does NOT duplicate CIP/address/employment
 * surface. This is exactly the "extend, don't rewrite" mandate.
 */

import { useCallback, useState } from 'react';
import { Banner, Button, ModalShell, GlobalCustomerSearch } from '@/components/primitives';
import type { CustomerSearchRow } from '@/components/primitives';
import { PersonDialog } from '@/modules/RAO/components/personDialog/PersonDialog';
import type { RaoPerson } from '@/modules/RAO/types';
import { useJointOwnerAdd } from './useJointOwnerAdd';
import { useApi } from '../../hooks/useApi';
import type { AccountScopedMaintProps } from '../types';

export function JointOwnerAddDialog(props: AccountScopedMaintProps): JSX.Element | null {
  const {
    open, onClose, onSaved, cifId, acctId, acctType, prodDesc,
    officerCode = '000', readOnly = false,
  } = props;
  const { client } = useApi();
  const { run } = useJointOwnerAdd();

  const [step, setStep] = useState<'pick' | 'capture' | 'confirm'>('pick');
  const [selectedRow, setSelectedRow] = useState<CustomerSearchRow | null>(null);
  const [capturedPerson, setCapturedPerson] = useState<RaoPerson | null>(null);
  const [capturedCifId, setCapturedCifId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const [newPersonDialogOpen, setNewPersonDialogOpen] = useState<boolean>(false);

  const reset = useCallback((): void => {
    setStep('pick');
    setSelectedRow(null);
    setCapturedPerson(null);
    setCapturedCifId(null);
    setErr(null);
    setNewPersonDialogOpen(false);
  }, []);

  const handleClose = (): void => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleExistingSelect = (row: CustomerSearchRow): void => {
    if (row.cifId === cifId) {
      setErr('Selected CIF is the primary owner — cannot add as joint.');
      return;
    }
    setSelectedRow(row);
    setStep('confirm');
    setErr(null);
  };

  const handleNewPersonSave = useCallback(
    async (person: RaoPerson): Promise<void> => {
      // PersonDialog's onSave passes back the fully edited person; the dialog
      // has already called CustAdd via useSaveDispatch, so person.cifId should
      // be populated. If not (older dialog build), we call CustAdd explicitly.
      let newCif = person.cifId ?? null;
      if (!newCif) {
        // Fallback — unusual: PersonDialog normally owns the CustAdd. We still
        // try once in case a dialog returned pre-save.
        // NOTE: this path is a safety net; the typical path has cifId populated.
        setErr('Person was captured but no CIF was returned — retry from CIF Search.');
        setNewPersonDialogOpen(false);
        return;
      }
      setCapturedPerson(person);
      setCapturedCifId(newCif);
      setNewPersonDialogOpen(false);
      setStep('confirm');
    },
    [],
  );

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setErr(null);
    const jointCifId = selectedRow?.cifId ?? capturedCifId;
    const jointName = selectedRow?.name ?? capturedPerson?.fullName;
    if (!jointCifId) {
      setErr('No joint CIF selected.');
      setSubmitting(false);
      return;
    }
    const result = await run({
      primaryCifId: cifId,
      jointCifId,
      acctId,
      acctType,
      officerCode,
      newCif: Boolean(capturedCifId),
      jointDisplayName: jointName ?? `CIF ${jointCifId}`,
    });
    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error ?? 'Unknown error');
      return;
    }
    onSaved?.();
    reset();
    onClose();
    // Silence unused-var lint for client in case future work needs it here.
    void client;
  };

  if (!open) return null;

  const headerTitle = `Add Joint Owner — ${acctId}${prodDesc ? ' (' + prodDesc + ')' : ''}`;

  return (
    <>
      <ModalShell
        open={open && !newPersonDialogOpen}
        onClose={handleClose}
        title={headerTitle}
        size="lg"
        footer={
          step === 'confirm' ? (
            <>
              <Button variant="ghost" onClick={() => setStep('pick')} disabled={submitting}>Back</Button>
              {!readOnly && (
                <Button variant="primary" onClick={() => { void handleConfirm(); }} disabled={submitting}>
                  {submitting ? 'Linking…' : 'Confirm & Link'}
                </Button>
              )}
            </>
          ) : (
            <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          )
        }
      >
        {readOnly && (
          <Banner tone="warn" title="Read-only">
            You do not have the Account-Relationship-Add grant.
          </Banner>
        )}

        {err && <Banner tone="err" title="Unable to link">{err}</Banner>}

        {step === 'pick' && (
          <>
            <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>
              Search for an existing customer to add as Joint Owner on account{' '}
              <code style={{ fontFamily: 'var(--font-mono)' }}>{acctId}</code>, or capture a new person.
            </div>
            <GlobalCustomerSearch
              onSelect={handleExistingSelect}
              onAddNew={() => { setNewPersonDialogOpen(true); }}
              placeholder="Search name, SSN, or CIF"
              compact
            />
          </>
        )}

        {step === 'confirm' && (
          <>
            <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>Review and confirm the Joint Owner link.</div>
            <div style={{ border: '1px solid var(--ink-100)', borderRadius: 'var(--r-sm)', padding: 'var(--sp-5)', background: 'var(--surface-subtle)' }}>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                Joint Owner
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--pcb-blue-dark)', marginTop: 4 }}>
                {selectedRow?.name ?? capturedPerson?.fullName ?? '—'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>
                CIF <code style={{ fontFamily: 'var(--font-mono)' }}>{selectedRow?.cifId ?? capturedCifId}</code>
                {capturedCifId && ' · newly captured'}
              </div>
            </div>
            <div style={{ border: '1px solid var(--ink-100)', borderRadius: 'var(--r-sm)', padding: 'var(--sp-5)' }}>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                Account
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--pcb-blue-dark)', marginTop: 4 }}>
                {acctId}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>
                RelCode <strong>JO</strong> · primary CIF {cifId}
              </div>
            </div>
            <Banner tone="info" title="Signature card task will be auto-created">
              Procedure: reprint signature card and collect all signers.
            </Banner>
            {/* TODO: wire disclosure packet when Team 2's engine lands
                (updated signature card + joint CIP acknowledgment). */}
          </>
        )}
      </ModalShell>

      {/* New-person capture — reuses the unified PersonDialog. */}
      {newPersonDialogOpen && (
        <PersonDialog
          open
          mode="new"
          person={{
            role: 'joint',
            mode: 'new',
            firstName: '',
            lastName: '',
            fullName: '',
            customerType: 'Individual',
          }}
          onSave={(p) => { void handleNewPersonSave(p); }}
          onClose={() => { setNewPersonDialogOpen(false); }}
          cartId={cifId}
          officerCode={officerCode}
        />
      )}
    </>
  );
}
